import { useEffect, useRef, useState } from "react";

import {
  AGENTIC_LOGIC_PUBLISH_GATE_ORDER,
  type AgenticLogicPublishGateResultDto,
  type AgenticLogicRulePublishJobDto,
} from "@intelligems/ig-types/dtos/agentic-logic/publish";
import { type AgenticLogicActiveRepairRun } from "@intelligems/ig-types/dtos/agentic-logic/repair";
import { useQueryClient } from "@tanstack/react-query";

import { agenticLogicKeys } from "../queryKeys";

import { useAgenticLogicRulePublishJob } from "./publish-agentic-logic-rule";

/**
 * Session storage keeps a manually-started job alive before the rule-detail
 * query refreshes. The durable fallback is the latest job returned with rule
 * detail — for every rule state, not only drafts — so automatic publication and
 * failed jobs survive a reload too, and a live rule keeps showing the gate run
 * that shipped it.
 *
 * Hydrated history is state, not an event: cache invalidation stays scoped to
 * runs this session started or watched. The success toast is the rule page's
 * (`usePublishAnnouncement`), which knows how many experiences it reached.
 */
export const agenticLogicPublishJobStorageKey = (ruleId: string) =>
  `agentic-logic:publish-job:${ruleId}`;

const readStoredJobId = (ruleId: string | undefined): string | null => {
  if (!ruleId) {
    return null;
  }
  try {
    return sessionStorage.getItem(agenticLogicPublishJobStorageKey(ruleId));
  } catch {
    // Storage can be unavailable (Safari private mode, blocked cookies). Losing
    // reload-resume is acceptable; failing the publish flow is not.
    return null;
  }
};

const writeStoredJobId = (ruleId: string, jobId: string): void => {
  try {
    sessionStorage.setItem(agenticLogicPublishJobStorageKey(ruleId), jobId);
  } catch {
    // See readStoredJobId.
  }
};

const clearStoredJobId = (ruleId: string): void => {
  try {
    sessionStorage.removeItem(agenticLogicPublishJobStorageKey(ruleId));
  } catch {
    // See readStoredJobId.
  }
};

/**
 * Read the stored job id and, if there is one, record it as a run this session
 * owns: only a session that started a publish ever writes that key.
 */
const claimStoredJobId = (
  ruleId: string | undefined,
  sessionRunJobIds: Set<string>
): string | null => {
  const storedJobId = readStoredJobId(ruleId);
  if (storedJobId) {
    sessionRunJobIds.add(storedJobId);
  }
  return storedJobId;
};

/** A repair owns all of its attempts, including the final job after it settles. */
const resolveTrackedJobId = (
  storedJobId: string | null,
  latestJobId: string | null,
  run: AgenticLogicActiveRepairRun | null | undefined
): string | null => {
  const owns = (id: string | null) =>
    id !== null &&
    (run?.currentPublishJobId === id ||
      run?.attempts.some((attempt) => attempt.publishJobId === id));
  if (
    run?.currentPublishJobId &&
    (!storedJobId || owns(storedJobId)) &&
    (run.finishedAtTs === null || !latestJobId || owns(latestJobId))
  ) {
    return run.currentPublishJobId;
  }
  return storedJobId ?? latestJobId;
};

/**
 * Remember a publish job for a rule whose progress hook does not exist yet.
 *
 * The creation flow starts a job and immediately navigates to the new rule's
 * page, which mounts {@link useAgenticLogicPublishProgress} fresh — without
 * this the gate rail would wait for the rule-detail read to catch up before it
 * could say anything.
 */
export const rememberAgenticLogicPublishJob = (
  ruleId: string,
  jobId: string
): void => writeStoredJobId(ruleId, jobId);

export interface AgenticLogicPublishProgress {
  /** The job being followed, resumed from session storage where possible. */
  trackedJobId: string | null;
  job: AgenticLogicRulePublishJobDto | undefined;
  /**
   * Every gate in canonical order, always — gates the server has not reported
   * yet read as `pending`, so the rail is complete before the first poll
   * lands and never reorders as results arrive.
   */
  gates: AgenticLogicPublishGateResultDto[];
  isPublishing: boolean;
  hasProgressFrame: boolean;
  /**
   * `true` when this session started or watched the tracked run — i.e. its
   * outcome is an event worth announcing. A run hydrated from durable rule
   * detail is history: it must be shown, and must not be announced.
   */
  isSessionRun: boolean;
  trackJob: (jobId: string) => void;
  clearTrackedJob: () => void;
}

/**
 * Drives the publish gate rail by polling. There is no Agentic Logic
 * websocket topic, so this is net-new rather than a reuse of the
 * shipping-provider progress path.
 */
export const useAgenticLogicPublishProgress = (
  ruleId: string | undefined,
  latestPublishJob?: AgenticLogicRulePublishJobDto | null,
  activeRepairRun?: AgenticLogicActiveRepairRun | null
): AgenticLogicPublishProgress => {
  const queryClient = useQueryClient();
  // While a repair run is live, its current job outranks the durable
  // latest-job read: the loop replaces the job under the rail on every
  // iteration, and the rail must follow the loop, not the history.
  //
  // The attempts the rail steps off of are NOT lost by this: they are served
  // on `AgenticLogicActiveRepairRun.attempts` (read back from the run's own
  // publish jobs) and rendered beside the live gates, so a refusal survives
  // the loop enqueueing the next attempt, the run converging, and a reload.
  const repairActive =
    activeRepairRun !== null &&
    activeRepairRun !== undefined &&
    activeRepairRun.finishedAtTs === null;
  // The runs this session owns. A stored id is by definition one this session
  // started, so reading storage also claims the run.
  const sessionRunJobIds = useRef<Set<string>>(new Set<string>());
  // Repair bookkeeping can settle after its publish job. That changes this
  // effect's dependencies without creating a new terminal event, so remember
  // which outcomes this hook already handled.
  const handledTerminalJobIds = useRef<Set<string>>(new Set<string>());

  const [trackedJobId, setTrackedJobId] = useState<string | null>(() =>
    resolveTrackedJobId(
      claimStoredJobId(ruleId, sessionRunJobIds.current),
      latestPublishJob?.id ?? null,
      activeRepairRun
    )
  );

  // Re-sync when the page switches rules, the repair loop moves to its next
  // job, or the asynchronous detail read finds a durable job. A session job
  // wins until the server catches up.
  useEffect(() => {
    setTrackedJobId(
      resolveTrackedJobId(
        claimStoredJobId(ruleId, sessionRunJobIds.current),
        latestPublishJob?.id ?? null,
        activeRepairRun
      )
    );
  }, [latestPublishJob?.id, activeRepairRun, ruleId]);

  const { data } = useAgenticLogicRulePublishJob(
    ruleId,
    trackedJobId ?? undefined
  );
  const job =
    data?.job ??
    (trackedJobId === latestPublishJob?.id ? latestPublishJob : undefined);
  const jobId = job?.id;
  const jobStatus = job?.status;
  const jobMode = job?.mode;
  const isPublishing = jobStatus === "queued" || jobStatus === "running";

  // Observation and terminal handling share ONE effect, observation first, so
  // a run that jumps `running → succeeded` inside a single commit is still
  // recognised as this session's and still announced.
  useEffect(() => {
    if (!ruleId || !jobId || !jobStatus) {
      return;
    }
    if (jobStatus === "queued" || jobStatus === "running") {
      // Watching a run in flight makes its outcome this session's news.
      sessionRunJobIds.current.add(jobId);
      return;
    }

    // A terminal job this session never watched is history hydrated from rule
    // detail: it belongs on the rail, but announcing it would replay an old
    // publish on every reload.
    if (!sessionRunJobIds.current.has(jobId)) {
      return;
    }
    if (handledTerminalJobIds.current.has(jobId)) {
      return;
    }
    handledTerminalJobIds.current.add(jobId);

    // Terminal: the rail stays on screen for this session, but a later reload
    // should start clean rather than resurrecting a finished job.
    clearStoredJobId(ruleId);

    // Whatever the outcome, the generation gate may have written a new suite
    // onto the draft before the run ended, and the editor's generated test
    // file reads it from this inventory.
    void queryClient.invalidateQueries({
      queryKey: agenticLogicKeys.generatedTests(ruleId),
    });

    if (jobStatus === "succeeded") {
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.versions(ruleId),
      });
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
      // The draft too: a save with no graph regenerates one asynchronously,
      // and that worker runs alongside the publish — so the selection-logic
      // canvas is stale exactly when a reader has just been sent to look at it.
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.draft(ruleId),
      });
      // A dry run promotes nothing. A real publish is announced by the rule
      // page that asked for it ("Applied · vN is live in N experiences"),
      // which knows how many experiences it reached; this only refreshes.
      if (jobMode === "publish") {
        // The publish is the draft -> active flip, so the org's rule LIST
        // changed too: a page that already has it mounted (the rules tab, an
        // open binding picker) must learn the new state without a reload.
        void queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rules(),
        });
      }
      if (repairActive) {
        // The loop's verdict lives on the run row; refresh it alongside.
        void queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rule(ruleId),
        });
      }
    }

    if (jobStatus === "failed" || jobStatus === "cancelled") {
      // The rule detail carries `latestPublishJob`, so the workspace must
      // learn the terminal state even when this poller found it first. A
      // cancel also stands the repair run down, which only rule detail says.
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
      // A failed attempt is not the operation's outcome. The persistent
      // checks row reports a final failure after repair has finished.
    }
  }, [ruleId, jobId, jobMode, jobStatus, queryClient, repairActive]);

  const gates = AGENTIC_LOGIC_PUBLISH_GATE_ORDER.map(
    (gateKey): AgenticLogicPublishGateResultDto =>
      job?.gates.find((gate) => gate.gateKey === gateKey) ?? {
        gateKey,
        status: "pending",
      }
  );

  return {
    trackedJobId,
    job,
    gates,
    isPublishing,
    hasProgressFrame: job !== undefined,
    isSessionRun:
      trackedJobId !== null && sessionRunJobIds.current.has(trackedJobId),
    trackJob: (nextJobId: string) => {
      if (ruleId) {
        writeStoredJobId(ruleId, nextJobId);
      }
      sessionRunJobIds.current.add(nextJobId);
      setTrackedJobId(nextJobId);
    },
    clearTrackedJob: () => {
      if (ruleId) {
        clearStoredJobId(ruleId);
      }
      setTrackedJobId(null);
    },
  };
};
